// eslint-disable-next-line no-restricted-imports
import { type Editor, type Extensions, type FocusPosition, useEditor } from "@tiptap/react";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

import { debounce_input_delay, max_debounce_delay } from "types/constants";

import { type KeyOfOrString } from "utilities/type/PropsOfType";

import { isElementWithinEditor } from "commonComponents/btWrappers/editor/core/editor.utils";
import { type EditorControlMode } from "commonComponents/btWrappers/editor/core/extensions/custom/OverrideTopLevelEnterKeyExtension";
import {
    type EditorStateAction,
    type IEditor,
    type IEditorState,
} from "commonComponents/btWrappers/editor/core/useBTEditor.types";
import {
    currentSchemaVersion,
    type EditorContent,
    editorStorageKeys,
} from "commonComponents/btWrappers/editor/editor.types";
import { type IInputDebouncingContext } from "commonComponents/utilities/Input/InputDebouncingContext";

function reducer(state: IEditorState, action: EditorStateAction): IEditorState {
    switch (action.type) {
        case "setShowLinkEditorOverlay":
            return { ...state, showLinkEditor: action.payload };
        case "setShowLinkOptionsOverlay":
            return { ...state, showLinkOptions: action.payload };
        case "setShowBrowseImagesOverlay":
            return { ...state, showImageUpload: action.payload };
        case "setControlBarMode":
            return { ...state, mode: action.payload };
        default:
            throw new Error(`Action ${(action as EditorStateAction).type} not implemented.`);
    }
}

/**
 * Hook for providing the TipTap editor object, which is used to manage the state and control the
 * editor. Only use this hook if UX requires a specific layout of the editor.
 * @param id The id for the change/blur events
 * @param extensions Extensions to use for the editor. This configures the editor's functionality.
 *
 * Note: You should not be entering a custom list here, but instead should be using the
 * `get<FeatureSet>Extensions()` utilities.
 * @param readOnly If true, sets the editor in readonly mode
 * @param editorValue The value of the editor. Because replacing editor content is expensive from a performance perspective, after the first render,
 * the only way to modify the editor content is by changing the UUID of the editorValue. That way the component is still controlled, but we
 * avoid unnecessary performance hits when the content changes frequently (like when typing).
 * @param onChange Pass in formik's setFieldValue
 * @param onBlur Pass in formik's setFieldTouched
 * @param autoFocus If set, the editor's content will be autofocused when the editor is mounted for the first time
 * @param debouncingContext Debouncing configuration overrides. May be useful for testing purposes
 */
export function useBTEditor<FormValues = undefined>(
    id: KeyOfOrString<FormValues>,
    extensions: Extensions,
    readOnly: boolean,
    editorValue: EditorContent,
    mode: EditorControlMode,
    onChange: (id: KeyOfOrString<FormValues>, value: EditorContent) => void,
    onBlur: (id: KeyOfOrString<FormValues>, touched: boolean, shouldValidate?: boolean) => void,
    autoFocus?: FocusPosition,
    debouncingContext?: IInputDebouncingContext,
    onFocus?: () => void,
): IEditor | null {
    const [editorState, editorStateDispatch] = useReducer(reducer, {
        showLinkEditor: false,
        showLinkOptions: false,
        showImageUpload: false,
        mode: mode,
    });
    if (editorValue.schemaVersion !== currentSchemaVersion) {
        throw new Error(
            "There was an error loading the rich text editor. Please refresh the page or contact support if this issue persists.",
        );
    }
    const handleChange = useCallback(
        (id: KeyOfOrString<FormValues>, editor: Editor) => {
            onChange(id, {
                replyContent: editorValue.replyContent,
                content: editor.getJSON(),
                schemaVersion: currentSchemaVersion,
                _uuid: editorValue._uuid,
            });
        },
        [editorValue._uuid, editorValue.replyContent, onChange],
    );

    const debounced = useDebouncedCallback(
        handleChange,
        (debouncingContext && debouncingContext.inputDelay) || debounce_input_delay,
        { maxWait: (debouncingContext && debouncingContext.maxDelay) ?? max_debounce_delay },
    );

    useEffect(() => {
        // Check if debounced is currently pending
        // If it is, call flush() to manually execute the callback
        // Necessary for when a component will potentially unmount before the debounce finishes
        return () => {
            if (debounced.pending()) {
                debounced.flush();
            }
        };
    }, [debounced]);

    const editorAttributes = useMemo(() => {
        let baseProps: { [k: string]: string } = {
            role: "textbox",
            "aria-multiline": "true",
        };
        if (readOnly) {
            baseProps = { ...baseProps, "aria-readonly": "true" };
        }
        return baseProps;
    }, [readOnly]);

    const tiptapEditor = useEditor({
        immediatelyRender: true,
        // Need to refactor a lot of state/prop-drilling to set this to false.
        shouldRerenderOnTransaction: true,
        extensions,
        editable: !readOnly,
        content: editorValue.content,
        onUpdate: ({ editor }) => {
            debouncingContext?.useDebouncing
                ? debounced.callback(id, editor)
                : handleChange(id, editor);
        },
        onBlur: ({ event }) => {
            // Only fire blur if focus has truly left the editor ecosystem (not just moving to controls/overlays)
            // Check the relatedTarget to see where focus is moving to
            if (!isElementWithinEditor(event.relatedTarget)) {
                // NOTE: VERY IMPORTANT TO SET SHOULD VALIDATE TO FALSE. Otherwise you may run into a
                // race condition on stuff like BTEditorChat (https://dev.azure.com/buildertrend/BuildertrendProjects/_workitems/edit/177870/)
                // Ex:
                // 1. You "focus" on the Send button
                // 2. InputRecaptureHack fires editor.focus() to recapture focus back onto the editor
                // 3. You click on the "Send button"
                // 4. CommentContainer attempts to send the message, but validates before doing so using Formik's validateForm
                // 5. Due to some timing as a result of step 1a and the delay when Prosemirror sends the blur transaction the editor fires the blur event during the existing async Formik validateForm call made from CommentContainer
                // 6. This is a problem, because the editor's onBlur event for Chat is tied to the formik's setFieldTouched
                // 7. setFieldTouched actually triggers Formik's validation, which cancels the validateForm call, throwing a validation error indicating that it was cancelled
                // 8. The message doesn't get sent
                onBlur(id, true, false);
            }
        },
        onFocus: () => onFocus?.(),
        editorProps: {
            attributes: editorAttributes,
        },
        autofocus: autoFocus ?? false,
    });

    useEffect(() => {
        if (tiptapEditor && !tiptapEditor.isDestroyed && tiptapEditor.isEditable !== !readOnly) {
            tiptapEditor.setEditable(!readOnly);
        }
    }, [readOnly, tiptapEditor]);

    const uuid = useRef(editorValue._uuid ?? "0");
    useEffect(() => {
        const currentUUID = editorValue._uuid ?? "0";
        if (tiptapEditor && !tiptapEditor.isDestroyed && uuid.current !== currentUUID) {
            tiptapEditor.commands.setContent(editorValue.content, { emitUpdate: false });
            uuid.current = currentUUID;
        }
    }, [editorValue._uuid, editorValue.content, tiptapEditor]);

    useEffect(() => {
        if (!tiptapEditor) return;
        // TipTap storage is intentionally mutable shared state — extensions read these keys
        // during their own render/event cycles to access editor UI state and the dispatcher.
        // eslint-disable-next-line react-hooks/immutability -- TipTap storage is intentionally mutable; see comment above.
        tiptapEditor.storage[editorStorageKeys.editorState] = editorState;
        tiptapEditor.storage[editorStorageKeys.editorStateDispatch] = editorStateDispatch;
    }, [tiptapEditor, editorState, editorStateDispatch]);

    if (!tiptapEditor) {
        return null;
    }

    return {
        tiptapEditor,
        editorState,
        editorStateDispatch,
    };
}
